import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import apiService from '../../../services/api';
import './MobileCreateVoice.css';

const IconUpload = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
    <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
  </svg>
);

const IconFile = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/>
    <polyline points="13 2 13 9 20 9"/>
  </svg>
);

export default function MobileCreateVoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || '/m/voices';
  const from     = location.state?.from;

  const [name, setName]     = useState('');
  const [gender, setGender] = useState('unknown');

  // ── Upload state ──
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  // ── Submit state ──
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [processing, setProcessing]   = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState(false);
  const [createdName, setCreatedName] = useState('');

  // ── File selection ──
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const allowedExts = /\.(mp3|wav|m4a|aac|ogg|flac)$/i;
    const allowedMimes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/mp4', 'audio/aac', 'audio/ogg', 'audio/flac', 'audio/x-m4a', 'audio/x-flac'];
    const isAudio = allowedMimes.includes(f.type) || allowedExts.test(f.name);
    if (!isAudio) {
      setError('不支持此格式，请使用 MP3、WAV 或 M4A 文件');
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setError('音频文件不能超过 50MB');
      return;
    }
    setUploadFile(f);
    setError('');
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!uploadFile) { setError('请先选择音频文件'); return; }
    if (!name.trim()) { setError('请输入声音名称'); return; }

    setUploading(true);
    setError('');
    setProgress(0);

    try {
      const fd = new FormData();
      fd.append('file', uploadFile);

      let fileKey = '';
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const resp = JSON.parse(xhr.responseText);
              fileKey = resp.data?.fileKey || resp.fileKey || resp.url || '';
              resolve();
            } catch {
              reject(new Error('上传响应解析失败'));
            }
          } else {
            reject(new Error(`上传失败 (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error('上传网络错误'));
        xhr.open('POST', '/api/upload-audio');
        xhr.withCredentials = true;
        xhr.send(fd);
      });

      setProgress(100);
      setUploading(false);
      setProcessing(true);

      const result = await apiService.createAudioClone({
        fileKey,
        name: name.trim(),
        gender,
      });

      if (result && (result.code === 0 || result.code === 200 || result.success)) {
        setCreatedName(name.trim());
        setSuccess(true);
      } else {
        setError(result?.message || result?.msg || '克隆失败，请重试');
      }
    } catch (err) {
      setError(err.message || '网络错误，请重试');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const handleDone = () => navigate(from || returnTo, { replace: true });

  const canSubmit = !!uploadFile && !!name.trim() && !uploading && !processing;

  return (
    <MobileLayout title="克隆声音" showBack>
      <div className="mcv-root">
        {success ? (
          <div className="mcv-success">
            <div className="mcv-success-icon">🎙️</div>
            <div className="mcv-success-title">声音克隆中！</div>
            <div className="mcv-success-sub">
              「{createdName}」已提交处理，通常需要数分钟。完成后可在「我的声音」中找到。
            </div>
            <button className="mcv-success-btn" onClick={handleDone}>
              返回选择声音
            </button>
          </div>
        ) : (
          <>
            {/* Audio file upload */}
            <div className="mcv-card">
              <div className="mcv-card-title">上传音频文件</div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,audio/mpeg,audio/wav,audio/mp4,audio/aac,audio/ogg,audio/flac"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              <div className="mcv-upload-guide">
                {uploadFile ? (
                  <div className="mcv-file-selected">
                    <div className="mcv-file-icon"><IconFile /></div>
                    <div className="mcv-file-info">
                      <div className="mcv-file-name">{uploadFile.name}</div>
                      <div className="mcv-file-size">
                        {uploadFile.size < 1024 * 1024
                          ? `${(uploadFile.size / 1024).toFixed(0)} KB`
                          : `${(uploadFile.size / 1024 / 1024).toFixed(1)} MB`}
                      </div>
                    </div>
                    <button
                      className="mcv-file-clear"
                      onClick={() => { setUploadFile(null); setError(''); }}
                      aria-label="移除文件"
                    >×</button>
                  </div>
                ) : (
                  <>
                    <div className="mcv-upload-steps">
                      <div className="mcv-upload-step">
                        <span className="mcv-step-num">1</span>
                        <span>点击下方「选择文件」按钮</span>
                      </div>
                      <div className="mcv-upload-step">
                        <span className="mcv-step-num">2</span>
                        <span>
                          iOS 弹出菜单后选择 <strong>「浏览」</strong>；
                          Android 选择 <strong>「文件管理器」</strong>
                        </span>
                      </div>
                      <div className="mcv-upload-step">
                        <span className="mcv-step-num">3</span>
                        <span>找到并点击音频文件（MP3 / WAV / M4A 等）</span>
                      </div>
                    </div>
                    <button
                      className="mcv-upload-trigger-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <IconUpload />
                      选择文件
                    </button>
                  </>
                )}
              </div>

              {/* Progress */}
              {(uploading || progress > 0) && (
                <div className="mcv-progress-wrap">
                  <div className="mcv-progress-bar">
                    <div className="mcv-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mcv-progress-label">
                    {progress < 100 ? `上传中 ${progress}%` : '上传完成，处理中...'}
                  </div>
                </div>
              )}
              {processing && (
                <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', marginTop: 8 }}>
                  克隆处理中，请稍候...
                </div>
              )}
            </div>

            {/* Name */}
            <div className="mcv-card">
              <div className="mcv-card-title">声音名称</div>
              <input
                className="mcv-input"
                type="text"
                placeholder="给这个声音起个名字"
                maxLength={30}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Gender */}
            <div className="mcv-card">
              <div className="mcv-card-title">性别（选填）</div>
              <div className="mcv-gender-row">
                {[
                  { val: 'female',  label: '女声' },
                  { val: 'male',    label: '男声' },
                  { val: 'unknown', label: '未知' },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    className={`mcv-gender-btn${gender === val ? ' active' : ''}`}
                    onClick={() => setGender(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div className="mcv-reqs">
              <div className="mcv-reqs-title">🎤 音频质量要求</div>
              <ul>
                <li>安静环境录制，避免噪音</li>
                <li>朗读自然，语速适中</li>
                <li>建议内容长度 30 秒~3 分钟</li>
                <li>格式：MP3、WAV、M4A，最大 50MB</li>
              </ul>
            </div>

            {/* Error */}
            {error && <div className="mcv-error">{error}</div>}

            {/* Submit */}
            <button
              className="mcv-submit"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {uploading || processing ? <span className="mcv-spin" /> : '开始克隆'}
            </button>
          </>
        )}
      </div>
    </MobileLayout>
  );
}
